import { HttpAgent, type RunAgentInput } from "@ag-ui/client";
import { apiClient } from "@/lib/apiClient";

/**
 * AG-UI agent for the OneThing BFF, registered with CopilotKit via
 * `selfManagedAgents` (no CopilotKit runtime server — this is a Vite SPA).
 *
 * The BFF's `POST /api/chat/agui` accepts `{ message, context?, tools?,
 * toolResult? }`: it resolves the user from the session cookie and builds
 * the upstream OneClaw `RunAgentInput` (threadId, runId, identity headers)
 * itself. So `requestInit` collapses CopilotKit's full envelope down to:
 *  - the latest user message + ambient context (normal turn), or
 *  - the trailing `role:"tool"` result (Level 3 follow-up turn — CopilotKit
 *    re-runs the agent after executing a frontend tool handler so the LLM
 *    can respond to the result).
 * Registered frontend tools (`input.tools`) are forwarded on every turn;
 * OneClaw (Phase 04 FE-HITL) catalogs them and lets the LLM call them via
 * its `ag_ui_call` proxy. The response is a spec-shaped AG-UI SSE stream
 * that HttpAgent parses natively.
 *
 * NOTE: no subclass instance fields — `AbstractAgent.clone()` copies a
 * hardcoded property list and would drop them (CopilotKit clones agents
 * per run).
 */
class OneThingAguiAgent extends HttpAgent {
  protected override requestInit(input: RunAgentInput): RequestInit {
    const base = super.requestInit(input);
    return {
      ...base,
      headers: {
        ...(base.headers as Record<string, string>),
        // CopilotKit core overwrites `agent.headers` with its provider-level
        // `headers` config on registration (applyHeadersToAgent), wiping the
        // constructor headers — the api key must be re-applied here, at the
        // last moment before fetch, or the BFF rejects with INVALID_API_KEY.
        "x-api-key": import.meta.env.VITE_API_KEY || "",
      },
      credentials: "include",
      body: JSON.stringify(buildBffBody(input)),
    };
  }
}

interface BffAguiBody {
  message?: string;
  context?: { description: string; value: string }[];
  tools?: { name: string; description?: string; parameters?: unknown }[];
  toolResult?: { toolCallId: string; content: string };
}

function buildBffBody(input: RunAgentInput): BffAguiBody {
  const body: BffAguiBody = {};

  // CopilotKit inserts the tool-result message right after its paired
  // assistant toolCalls message, NOT at the end of the array (the
  // assistant's streamed text can trail it). So "is this a frontend-tool
  // follow-up turn?" = "is the newest tool message newer than the newest
  // user message?" — checking only the last element misses it, re-sends the
  // original user message, and makes the LLM call the tool in a loop.
  const lastUserIdx = findLastIndexByRole(input.messages, "user");
  const lastToolIdx = findLastIndexByRole(input.messages, "tool");

  if (lastToolIdx > lastUserIdx) {
    // Frontend-tool follow-up: the handler already ran; send its result so
    // the LLM (which deferred via ag_ui_call) can continue the turn.
    // ASSUMPTION: only the newest tool message is forwarded. CopilotKit's
    // per-tool run loop re-runs the agent after each frontend tool resolves,
    // so there is exactly one fresh tool result per follow-up turn. If that
    // loop ever batches multiple tool calls into one turn, earlier results
    // would be dropped here and the BFF contract would need a toolResult[].
    const toolMsg = input.messages[lastToolIdx] as { toolCallId: string; content: unknown };
    body.toolResult = {
      toolCallId: toolMsg.toolCallId,
      content:
        typeof toolMsg.content === "string" ? toolMsg.content : JSON.stringify(toolMsg.content),
    };
  } else {
    body.message = extractLatestUserMessage(input);
  }

  // App context registered via useAgentContext (CopilotReadables) → OneClaw
  // "## Ambient Context" prompt block.
  if (input.context?.length) body.context = input.context;

  // Frontend tools registered via useFrontendTool (CopilotFrontendTools) →
  // OneClaw FE-HITL catalog. parameters is already JSON Schema (CopilotKit
  // converts zod before runAgent).
  if (input.tools?.length) {
    body.tools = input.tools.map((t) => ({
      name: t.name,
      ...(t.description ? { description: t.description } : {}),
      ...(t.parameters ? { parameters: t.parameters } : {}),
    }));
  }

  return body;
}

/** Index of the newest message with the given role, or -1. */
function findLastIndexByRole(messages: RunAgentInput["messages"], role: string): number {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === role) return i;
  }
  return -1;
}

/**
 * CopilotKit v2 can emit user message content as a multimodal array
 * (`[{ type: "text", text }]`); the BFF only accepts a plain string.
 */
function extractLatestUserMessage(input: RunAgentInput): string {
  for (let i = input.messages.length - 1; i >= 0; i--) {
    const msg = input.messages[i];
    if (msg.role !== "user") continue;
    const content: unknown = msg.content;
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
      return content
        .filter(
          (part): part is { type: "text"; text: string } =>
            typeof part === "object" &&
            part !== null &&
            (part as { type?: string }).type === "text" &&
            typeof (part as { text?: unknown }).text === "string",
        )
        .map((part) => part.text)
        .join("\n");
    }
  }
  return "";
}

export function buildOneThingAgent(): HttpAgent {
  return new OneThingAguiAgent({
    // Dev: same-origin relative path so Vite proxies /api/* to VITE_API_URL
    // (vite.config.ts). buildURL would emit the cross-origin VITE_API_URL,
    // bypass the proxy, and break on CORS.
    // Staging/prod: buildURL() so the request hits the configured BFF origin
    // (absolute URL when VITE_API_URL is cross-origin). This request sets
    // credentials: "include", so a cross-origin BFF must send
    // CORS-with-credentials headers.
    url: import.meta.env.DEV
      ? "/api/chat/agui"
      : apiClient.buildURL("/api/chat/agui"),
    headers: { "x-api-key": import.meta.env.VITE_API_KEY || "" },
  });
}
